import { type PackageListItem, type PackageListQuery, type PackageStatus } from '@utazzo/shared';
import { Globe2, Pencil } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { useDeletePackage, usePackagesList } from '../use-packages';

import { hiddenFilterId, type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';
import { SoftPill } from '@/components/soft-pill';
import { ViewOnSiteLink } from '@/components/view-on-site-link';
import { useCurrentUser } from '@/features/auth/use-auth';
import { humanEnum } from '@/lib/enum-labels';
import { packagePublicUrl } from '@/lib/website-urls';

const PACKAGE_STATUS_LABEL: Record<string, string> = {
  DRAFT: 'Draft',
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  ARCHIVED: 'Archived',
};

const STATUS_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'ARCHIVED', label: 'Archived' },
];

const SCOPE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'Domestic + International' },
  { value: 'domestic', label: 'Domestic only' },
  { value: 'international', label: 'International only' },
];

const ARCHIVED_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'Hide archived' },
  { value: 'show', label: 'Show archived' },
];

const MIN_SEARCH_LENGTH = 3;

const FILTERS: ReadonlyArray<DataGridFilter> = [
  { id: 'status', label: 'Status', options: STATUS_OPTIONS, widthClass: 'w-48' },
  { id: 'scope', label: 'Scope', options: SCOPE_OPTIONS, widthClass: 'w-56' },
  { id: 'archived', label: 'Archived', options: ARCHIVED_OPTIONS, widthClass: 'w-44' },
  // Deep-link targets for the website dashboard's breakdown tables. Hidden: no
  // chip, just a removable pill while one is active.
  { id: 'destination', label: 'Destination', options: [], hidden: true },
  { id: 'category', label: 'Category', options: [], hidden: true },
  { id: 'tripType', label: 'Trip type', options: [], hidden: true },
];

const STATUS_TONE: Record<PackageStatus, string> = {
  DRAFT: 'border border-zinc-200 bg-zinc-50 text-zinc-700',
  ACTIVE: 'border border-emerald-200 bg-emerald-50 text-emerald-700',
  INACTIVE: 'border border-amber-200 bg-amber-50 text-amber-700',
  ARCHIVED: 'border border-rose-200 bg-rose-50 text-rose-700',
};

const PaisePill = ({ value }: { value: string | null }): React.JSX.Element => {
  if (value === null) return <span className="text-zinc-400">-</span>;
  const rupees = Number(value);
  return (
    <span className="font-medium text-zinc-900">
      {rupees.toLocaleString('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0,
      })}
    </span>
  );
};

export const PackagesListPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const canDelete = me?.role === 'ADMIN';
  const deletePackage = useDeletePackage();
  const navigate = useNavigate();

  const columns: ListScreenColumns<PackageListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<PackageListItem>> = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'name',
        header: 'Package',
        sortable: true,
        cell: (row) => (
          <div className="flex flex-col">
            <span className="font-medium text-zinc-900">{row.name}</span>
            <ViewOnSiteLink href={packagePublicUrl(row.slug)} label={row.slug} />
            {row.shortDescription && (
              <span className="mt-0.5 max-w-md truncate text-xs text-zinc-500">
                {row.shortDescription}
              </span>
            )}
          </div>
        ),
      },
      {
        id: 'duration',
        header: 'Duration',
        width: '110px',
        cell: (row) => (
          <span className="text-zinc-700">
            {row.numberOfNights}N / {row.numberOfDays}D
          </span>
        ),
      },
      {
        id: 'category',
        header: 'Category',
        cell: (row) => (
          <div className="flex items-center gap-1.5 text-zinc-600">
            {row.isInternational && <Globe2 className="h-3.5 w-3.5" aria-hidden />}
            <span>{row.packageCategory ?? '-'}</span>
          </div>
        ),
      },
      {
        id: 'price',
        header: 'Price',
        sortable: true,
        width: '120px',
        cell: (row) => <PaisePill value={row.price} />,
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => (
          <div className="flex items-center gap-2">
            <SoftPill className={STATUS_TONE[row.status]}>
              {humanEnum(PACKAGE_STATUS_LABEL, row.status)}
            </SoftPill>
          </div>
        ),
      },
    ];
    return cols;
  };

  return (
    <ListScreen<PackageListItem, PackageListQuery>
      title="Packages"
      description="Operator-side SKU catalog. Agents clone from packages when building itineraries; the website renders the same rows via the marketing surface."
      useList={usePackagesList}
      defaultSort={{ by: 'createdAt', dir: 'desc' }}
      minSearchLength={MIN_SEARCH_LENGTH}
      toQuery={(state, base) => {
        const status = state.filters.status;
        const scope = state.filters.scope;
        const archived = state.filters.archived;
        const destinationId = hiddenFilterId(state.filters.destination);
        const categorySlug = hiddenFilterId(state.filters.category);
        const tripTypeId = hiddenFilterId(state.filters.tripType);
        return {
          ...base,
          ...(destinationId ? { destinationId } : {}),
          ...(categorySlug ? { categorySlug } : {}),
          ...(tripTypeId ? { tripTypeId } : {}),
          ...(status ? { status: status as PackageStatus } : {}),
          ...(scope === 'international' ? { isInternational: true } : {}),
          ...(scope === 'domestic' ? { isInternational: false } : {}),
          ...(archived === 'show' ? { includeDeleted: true } : {}),
        };
      }}
      columns={columns}
      filters={FILTERS}
      getRowId={(row) => row.id}
      onRowClick={(row) => navigate(`/packages/${row.id}`)}
      createButton={{ label: 'Add package', onClick: () => navigate('/packages/new') }}
      rowActions={{
        extra: (row) => [
          {
            type: 'item',
            label: 'Edit',
            icon: <Pencil className="h-4 w-4" />,
            onSelect: () => navigate(`/packages/${row.id}`),
          },
        ],
        delete: {
          onDelete: (row) => deletePackage.mutateAsync(row.id),
          disabled: () => !canDelete,
          confirm: (row) => ({
            title: 'Delete this package?',
            description: `${row.name} will be soft-deleted. Itineraries already cloned from it are unaffected.`,
          }),
        },
      }}
      copy={{
        searchPlaceholder: 'Search by name, slug, description, or category (min 3 characters)',
        emptyTitle: 'No packages match these filters',
        emptyDescription:
          'Adjust the filters or clear the search box. Soft-deleted legacy packages are hidden by default; use the Archived filter to show them.',
        errorTitle: 'Failed to load packages',
        errorDescription: 'The server returned an error. Try refreshing or check your network.',
      }}
    />
  );
};
